import React, {
    Fragment,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Helmet } from "react-helmet";
import Layout from "../components/common/layout/Index";
import useTranslationHook from "../components/hooks/useTranslationHook";
import Breadcrumb from "../components/common/Breadcrumb";
import RegistrationType from "../components/partials/property-details/right-panel/bidder-registration/RegistrationType";
import { useDispatch, useSelector } from "react-redux";
import {
    BUYER_REGISTRATION_STEPS,
    RegistrationFlowType,
} from "../components/partials/property-details/right-panel/bidder-registration/constant";
import RegistrationDepositPayment from "../components/partials/property-details/right-panel/bidder-registration/RegistrationDepositPayment";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
    resetBuyerRegistration,
    updateBuyerRegistrationField,
} from "../redux/slice/buyerSlice";
import DocumentsUpload from "../components/partials/property-details/right-panel/bidder-registration/DocumentsUpload";
import DetailsReview from "../components/partials/property-details/right-panel/bidder-registration/DetailsReview";
import {
    fetchPropertyDetail,
    sellerAction,
} from "../redux/action/sellerAction";
import {
    fetchAuctionBidRegistrationDetail,
    fetchEmsignerStatus,
    fetchKycDetail,
    fetchRegistrationRejectReason,
} from "../redux/action/buyerAction";
import { toast } from "react-toastify";
import StatusPage from "../components/verification/Success";
import { createSlug } from "../helpers";
import { fetchLots } from "../redux/action/liveAuctionAction";
import { handleSelection } from "../redux/slice/liveAuctionSlice";
import LotSelection from "../components/partials/property-details/right-panel/bidder-registration/LotSelection";
import BiddingLimit from "../components/partials/property-details/right-panel/bidder-registration/BiddingLimit";
import { relayEmsignerCompletion } from "../utils";

export default function BidderRegistration() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { state } = location;
    const { t } = useTranslationHook();
    const [params, setSearchParams] = useSearchParams();

    const [modalType, setModalType] = useState(null);
    const [isResolved, setIsResolved] = useState(false);
    const shouldSkipFetchRef = useRef(false);
    const step = useSelector((state) => state.buyer.buyerRegistration.step);
    const registrationType = useSelector(
        (state) => state.buyer.buyerRegistration.registrationType
    );
    const lots = useSelector((state) => state.liveAuction.lots);
    const lang = useSelector((state) => state.translation.lang);
    const {
        propertyId,
        propertyName,
        propertyNameAR,
        currentPathName,
        selectedSessionID,
        selfRegistration,
        propertyAuctionDate,
        lotID,
        isBidLimitIncrease,
    } = state || {};
    const commonSteps = {
        [BUYER_REGISTRATION_STEPS.PAYMENT]: <RegistrationDepositPayment />,
        [BUYER_REGISTRATION_STEPS.LOTS_SELECTION]: <LotSelection />,
        [BUYER_REGISTRATION_STEPS.Bidding_Limit]: <BiddingLimit />,
    };

    const registrationStepComponents = {
        [RegistrationFlowType.PRINCIPAL]: {
            [BUYER_REGISTRATION_STEPS.PRINCIPAL_DOCS]: <DocumentsUpload />,
            [BUYER_REGISTRATION_STEPS.REVIEW]: <DetailsReview />,
            ...commonSteps,
        },

        [RegistrationFlowType.SELF]: {
            ...commonSteps,
        },
    };

    const StepComponent = useMemo(() => {
        if (!registrationType) return null;
        return registrationStepComponents[registrationType]?.[step] ?? null;
    }, [registrationType, step]);

    useLayoutEffect(() => {
        if (selectedSessionID) {
            dispatch(fetchLots({ session_id: selectedSessionID }));
        }
        // Fresh navigation into the increase flow — reset so previous session's value doesn't bleed through
        if (isBidLimitIncrease) {
            dispatch(handleSelection({ deposit_amount: 0 }));
        }
    }, []);

    // Notify parent EmsignerIframe when eMSigner redirects back into this page
    // inside the iframe, or a fallback new tab opened from it
    useEffect(() => {
        if (window === window.top && !window.opener) return;
        relayEmsignerCompletion({
            type: "EMSIGNER_SIGNED",
            search: window.location.search,
        });
    }, []);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const propertyId = searchParams.get("property_id");
        if (!propertyId) return;

        searchParams.delete("property_id");
        navigate(
            {
                pathname: location.pathname.replace(/\/$/, ""),
                search: params.toString() ? `?${params.toString()}` : "",
            },
            { replace: true }
        );

        (async () => {
            try {
                const response = await dispatch(
                    fetchPropertyDetail(propertyId)
                );
                const data = response?.data || {};
                if (!data) return;
                const slug = createSlug(
                    data.id,
                    `${data.property_name} ${data.country}`
                );
                navigate(location.pathname, {
                    replace: true,
                    state: {
                        propertyId: data.id,
                        depositAmount: data.deposit_amount,
                        propertyName: data.property_name,
                        propertyNameAR: data.property_name_ar,
                        // start_date: data.propertyAuctionDate,
                        propertyState: data.state,
                        propertyCommunity: data.community,
                        propertyCommunityAr: data.community_ar,
                        currentPathName: `${window.location.origin}/property/detail/${slug}`,
                    },
                });
            } catch (err) {
                console.error("Property fetch failed:", err);
            }
        })();
    }, [location.search, location.pathname, dispatch, navigate]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const success = params.get("success");
        const isValidSuccess = ["true", "false"].includes(success);

        if (!isValidSuccess) {
            setIsResolved(true);
            return;
        }

        params.delete("success");

        const propertyId = sessionStorage.getItem("property_id");
        const selectedLotID = sessionStorage.getItem("selectedLotID");
        const bidderRegType = sessionStorage.getItem("bidder_reg_type");
        const currentPathName = sessionStorage.getItem("currentPathName");
        const selectedSessionID = sessionStorage.getItem("selectedSessionID");
        const isBidLimitIncreaseSession =
            sessionStorage.getItem("isBidLimitIncrease") === "true";

        let updatedState = location.state || {};

        const hasSessionData = propertyId && currentPathName;
        const hasLiveData = selectedLotID && selectedSessionID;
        const isSuccess = success === "true";

        // if (isValidSuccess && (hasSessionData || hasLiveData) && (!hasLiveData && bidderRegType !== "1")) {
        //     shouldSkipFetchRef.current = true;
        // }

        if (
            isValidSuccess &&
            hasSessionData &&
            (hasLiveData || bidderRegType === "2")
        ) {
            shouldSkipFetchRef.current = true;
        }

        if (hasSessionData || hasLiveData) {
            updatedState = {
                ...updatedState,
                propertyId,
                currentPathName,
                selectedSessionID,
                lotID: selectedLotID,
                reg_type: bidderRegType,
            };

            [
                "property_id",
                "currentPathName",
                "selectedLotID",
                "selectedSessionID",
                "bidder_reg_type",
                "isBidLimitIncrease",
            ].forEach((key) => sessionStorage.removeItem(key));
        }
        navigate(
            {
                pathname: location.pathname.replace(/\/$/, ""),
                search: params.toString() ? `?${params.toString()}` : "",
            },
            { replace: true, state: updatedState }
        );

        if (!(bidderRegType === "1" && isSuccess && !hasLiveData)) {
            if (isSuccess && hasSessionData && isBidLimitIncreaseSession) {
                setModalType("bid_limit_success");
            } else if (isSuccess && hasSessionData) {
                setModalType("success");
            } else if (!isSuccess) {
                setModalType("error");
            }
        }

        setIsResolved(true);
    }, [location.search, location.pathname, location.state, navigate]);

    useEffect(() => {
        if (
            !isResolved ||
            (!propertyId && !selectedSessionID) ||
            shouldSkipFetchRef.current
        )
            return;

        dispatch(
            fetchAuctionBidRegistrationDetail({
                ...(selectedSessionID
                    ? {
                          session_id: selectedSessionID,
                          selfRegistration,
                      }
                    : { property_id: propertyId }),
            })
        )
            .then((response) => {
                const { code, error, data } = response || {};
                if (code !== 1 || error !== 0) {
                    return dispatch(
                        fetchKycDetail({ session_id: selectedSessionID })
                    );
                }
                const isValidAuction =
                    data?.is_payment_completed &&
                    data?.is_approved === 2 &&
                    data?.registration_id;
                if (currentPathName && isValidAuction && !isBidLimitIncrease) {
                    navigate(new URL(currentPathName).pathname, {
                        replace: true,
                    });
                }
                if (isBidLimitIncrease && isValidAuction) {
                    dispatch(
                        updateBuyerRegistrationField({
                            value: {
                                registration_id: data?.id,
                                registrationType:
                                    data?.registration_type === 1
                                        ? RegistrationFlowType.SELF
                                        : RegistrationFlowType.PRINCIPAL,
                                step: BUYER_REGISTRATION_STEPS.Bidding_Limit,
                            },
                        })
                    );
                }
            })
            .catch((err) => {
                console.error(err);
                dispatch(fetchKycDetail());
            });
        if (propertyId) dispatch(fetchRegistrationRejectReason({ propertyId }));
        //eslint-disable-next-line
    }, [propertyId, isResolved]);

    useEffect(() => {
        if (!propertyId) return;
        dispatch(
            updateBuyerRegistrationField({
                field: "property_id",
                value: propertyId,
            })
        );
    }, [propertyId]);

    useEffect(() => {
        // Inside the signing iframe this is a throwaway app instance — the
        // postMessage effect above already relays the redirect to the parent,
        // which is the only place this logic should actually run.
        if (window !== window.top) return;

        // Normalize search string — eMSigner may append ?workflowid=... even if
        // redirect URL already had ?esState=..., producing a double-?. Fix by
        // replacing any subsequent ? with & before parsing.
        const rawSearch = window.location.search;
        const normalizedSearch =
            rawSearch.slice(0, 1) + rawSearch.slice(1).replace(/\?/g, "&");
        const urlParams = new URLSearchParams(normalizedSearch);

        const workflowid = urlParams.get("workflowid");
        if (!workflowid) return;

        // Decode state that was embedded into the redirect URL by EmsignerIframe
        let esState = {};
        try {
            const raw = urlParams.get("esState");
            if (raw) esState = JSON.parse(atob(decodeURIComponent(raw)));
        } catch {
            esState = {};
        }

        const {
            at: agreementType,
            pid: propertyIdP,
            sid: sessionIdP,
            rid: registrationIdP,
            cp: currentPathP,
            lid: lotIDP,
        } = esState;

        const id = agreementType === "live_auction" ? sessionIdP : propertyIdP;

        urlParams.delete("workflowid");
        urlParams.delete("referenceno");
        urlParams.delete("esState");
        const cleanSearch = urlParams.toString()
            ? `?${urlParams.toString()}`
            : "";

        const restoredState = {
            ...(location.state || {}),
            propertyId: propertyIdP || undefined,
            selectedSessionID: sessionIdP || undefined,
            currentPathName: currentPathP || undefined,
            lotID: lotIDP || undefined,
        };

        if (!id || !agreementType || !registrationIdP) {
            navigate(
                { pathname: location.pathname, search: cleanSearch },
                { replace: true, state: restoredState }
            );
            return;
        }

        // Prevent the [propertyId, isResolved] effect from re-fetching after navigate
        shouldSkipFetchRef.current = true;

        dispatch(fetchEmsignerStatus(id, agreementType)).then(
            async (verifyRes) => {
                const status = verifyRes?.data?.user_sing_status;

                if (status === "completed" || status === "pending") {
                    // Signature/term acceptance is now recorded by the backend via
                    // eMSigner's webhook — just reload registration data so the
                    // PAYMENT step has what it needs and move forward.
                    shouldSkipFetchRef.current = false;
                    await dispatch(
                        fetchAuctionBidRegistrationDetail(
                            sessionIdP
                                ? { session_id: sessionIdP }
                                : { property_id: propertyIdP }
                        )
                    );
                    shouldSkipFetchRef.current = true;
                    toast.success("Agreement has been signed successfully")
                    dispatch(
                        updateBuyerRegistrationField({
                            value: {
                                step: BUYER_REGISTRATION_STEPS.PAYMENT,
                                isBidderRegistrationModalOpen: false,
                            },
                        })
                    );
                } else {
                    toast.info(
                        t(
                            "Agreement signing is pending. Please sign the agreement to proceed."
                        )
                    );
                }

                navigate(
                    { pathname: location.pathname, search: cleanSearch },
                    { replace: true, state: restoredState }
                );
            }
        );
        //eslint-disable-next-line
    }, [location.search]);

    useEffect(() => {
        return () => {
            dispatch(resetBuyerRegistration());
            dispatch(sellerAction.handleSetCountries([]));
        };
    }, []);

    const links = [
        { url: "/", name: t("Home") },
        {
            url: currentPathName,
            name: t("My Properties"),
            ...(selectedSessionID && {
                state: {
                    propertyId: propertyId,
                    propertyAuctionDate: propertyAuctionDate,
                    selectedSessionID: selectedSessionID,
                    lotID: lotID,
                },
            }),
        },
        {
            url: "",
            name: lang === "en" ? propertyName : propertyNameAR || "",
        },
    ];

    return (
        <>
            <Helmet>
                <meta
                    name="keywords"
                    content={t(
                        "Bidhome-Adres, CRE, brokers, investment, commercial real estate, sales, auction"
                    )}
                />
                <meta
                    name="description"
                    content={t(
                        "Bidhome-Adres brings buyers, sellers, and brokers together to efficiently market and close commercial real estate deals in online CRE auctions."
                    )}
                />
                <title>{t("Property Auction - My Property Page")}</title>
            </Helmet>
            <Layout>
                {!isResolved ? null : modalType === "bid_limit_success" ? (
                    <BidLimitIncreaseConfirmation
                        currentPathName={currentPathName}
                        onReturn={() => {
                            setModalType(null);
                            if (currentPathName)
                                navigate(new URL(currentPathName).pathname, {
                                    replace: true,
                                    state: {
                                        selectedSessionID,
                                        lotID,
                                        propertyId,
                                    },
                                });
                        }}
                        t={t}
                    />
                ) : modalType ? (
                    <StatusPage
                        type={modalType}
                        module="bidder_registration"
                        onClose={() => setModalType(null)}
                    />
                ) : (
                    <Fragment>
                        <Breadcrumb links={links} />
                        {step === BUYER_REGISTRATION_STEPS.DETAILS && (
                            <RegistrationType />
                        )}
                        {step !== BUYER_REGISTRATION_STEPS.DETAILS &&
                            StepComponent}
                    </Fragment>
                )}
            </Layout>
        </>
    );
}

function BidLimitIncreaseConfirmation({ onReturn, t }) {
    return (
        <div className="bid-limit-confirmation">
            <div className="confirmation-inner">
                <div className="confirmation-icon">
                    <img src="/img/check-L.svg" alt="" />
                </div>
                <h2>{t("Bidding limit increased")}</h2>
                <p>
                    {t(
                        "Be ready to place your bids the moment the live auction begins"
                    )}
                </p>
                <button
                    className="btn btn-primary btn-lg rounded-pill px-5"
                    onClick={onReturn}
                >
                    {t("Return to session details")}
                </button>
            </div>
        </div>
    );
}
